/**
 * The "Address Details" section of the address screen: the address itself,
 * the pickup or delivery window, the loading capabilities, and whether to keep
 * the location in the address book.
 *
 * Loading capabilities sit inside this section rather than beside it because
 * answering "no dock, no forklift" changes the price, and the requote offer
 * that follows has to appear in the same breath as the answers that caused it.
 */

import LocationsDockHoursQuestionBubble from "@/Shared/Locations/Bubbles/LocationsDockHoursQuestionBubble";
import LoadingCapabilitiesQuestionBubble from "@/Shared/Locations/Bubbles/LoadingCapabilitiesQuestionBubble";
import { LocationsFavouriteMarker } from "@/Shared/Locations/CustomComponents/LocationsFavouriteMarker";
import LoadingCapabilitiesDropdown from "@/Shared/Locations/Dropdowns/LoadingCapabilitiesDropdown";
import LocationsAddressInput from "@/Shared/Locations/Inputs/LocationsAddressInput";
import { LocationsRestrictedDockHoursInput } from "@/Shared/Locations/Inputs/LocationsRestrictedDockHoursInput";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Switch } from "@freightsimple/ui-core/Controls/Switch";
import { T } from "@freightsimple/ui-core/Typography/T";
import BookShipmentAddressQuestionBubble from "../../Bubbles/BookShipmentAddressQuestionBubble";
import BookShipmentRequoteWithLiftGateSection from "../BookShipmentRequoteWithLiftGateSection";
import BookShipmentSmallSection from "../BookShipmentSmallSection";
import type { useBookShipmentAddressLoadingCapabilities } from "../../Hooks/useBookShipmentAddressLoadingCapabilities";
import type { useBookShipmentAddressLocationEdits } from "../../Hooks/useBookShipmentAddressLocationEdits";
import type { useBookShipmentAddressValidation } from "../../Hooks/useBookShipmentAddressValidation";

interface BookShipmentAddressDetailsSectionProps {
  index: number;
  context: LocationContext;
  displayName: string;
  location: PreQuoteLocation;
  shipment: PreBookingShipment;
  savedLocations: Array<SavedLocation>;
  contact: Contact;
  referenceNumber: string;
  amazonReferenceId: string | undefined;
  amazonShipmentId: string | undefined;
  boothNumber: string;
  deadlineDate: string | undefined;
  forceValidation: boolean;
  isDeliveryAppointment: boolean;
  isSameDayPickup: boolean;
  isContactNotRequired: boolean;
  // What the switch shows: the customer's choice if they made one, otherwise
  // what the address book already says.
  favouriteSwitchChecked: boolean;
  // The customer's own choice, undefined until they touch the switch.
  markLocationAsFavourite: boolean | undefined;
  onMarkLocationAsFavourite: (newValue: boolean) => void;
  onModify: () => void;
  locationEdits: ReturnType<typeof useBookShipmentAddressLocationEdits>;
  loadingCapabilities: ReturnType<
    typeof useBookShipmentAddressLoadingCapabilities
  >;
  validation: ReturnType<typeof useBookShipmentAddressValidation>;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function BookShipmentAddressDetailsSection(
  props: BookShipmentAddressDetailsSectionProps,
) {
  const {
    context,
    displayName,
    location,
    shipment,
    forceValidation,
    locationEdits,
    loadingCapabilities,
    validation,
  } = props;
  const { locationErrors } = validation;

  return (
    <BookShipmentSmallSection
      header={
        <>
          <>Address Details</>
          <BookShipmentAddressQuestionBubble
            displayName={displayName}
            context={context}
            onModify={props.onModify}
            location={location}
          />
        </>
      }
      description={
        context === LocationContext.Pickup
          ? "Finish details of the address where the shipment will be picked up"
          : "Finish details of the address where the shipment will be delivered"
      }
      index={props.index}
    >
      <Panel collapseOnNarrow>
        <div className="flex w-full flex-col gap-6">
          <LocationsAddressInput
            enabled={true}
            businessName={location.businessName || ""}
            businessNameChanged={locationEdits.setBusinessName}
            locationType={location.locationType}
            distributionWarehouseBrand={location.distributionWarehouseBrand}
            address={location.address}
            addressChanged={locationEdits.updateAddress}
            forceValidation={forceValidation}
            businessNameErrorMessages={locationErrors.businessName}
            addressErrorMessages={{
              ...locationErrors.address,
              addressLine:
                locationErrors.address.addressLine ??
                validation.sameAddressAsPickupError(),
            }}
            addressOptions={locationEdits.addressOptions}
            displayName={displayName}
            allowCityAndPostalCodeEditing={false}
            warnAboutPotentialBusinessNameErrors={true}
          />
          {!props.isDeliveryAppointment && (
            <div className="flex w-full flex-col gap-2">
              <T size="base" weight="semibold">
                {context === LocationContext.Pickup
                  ? "Pickup Window"
                  : "Delivery Window"}{" "}
                <LocationsDockHoursQuestionBubble />
              </T>
              <LocationsRestrictedDockHoursInput
                openFrom={location.hours?.openFrom}
                openUntil={location.hours?.openUntil}
                setOpenFrom={locationEdits.setHoursOpenFrom}
                setOpenUntil={locationEdits.setHoursOpenUntil}
                context={context}
                restrictions={shipment.selectedQuote?.restrictions}
                pickupOpenUntil={shipment.pickupLocation?.hours?.openUntil}
                hoursErrorMessage={locationErrors.hours}
                forceValidation={forceValidation}
                warningMessage={validation.pickupHoursWarnings}
              />
              {props.isSameDayPickup && (
                /* No negative margin. It used to pull this up by 16px to eat the
                   dropdowns' reserved (empty) message line — which works only
                   while that line IS empty. The dock-hours warning is two lines
                   and always present when the cutoff is before 4pm, so the
                   margin dragged this note straight through it. Main left the
                   reserved space alone and put the note below it. */
                <div className="text-sm text-content-muted">
                  Note: For same day pickups, typically a carrier needs at least
                  3 hours notice to be able to pick up a shipment. If the
                  carrier is unable to pick it up, they will pick it up on the
                  next business day.
                </div>
              )}
            </div>
          )}

          {!loadingCapabilities.hasLiftGate && (
            <div className="flex w-full flex-col gap-4">
              <T size="base" weight="semibold">
                Loading Capabilities <LoadingCapabilitiesQuestionBubble />
              </T>
              <LoadingCapabilitiesDropdown
                question="Does this location have a shipping dock?"
                value={
                  loadingCapabilities.locationHasDock ??
                  LoadingCapabilitiesAvailability.Unknown
                }
                onChange={loadingCapabilities.onDockChange}
                errorMessage={loadingCapabilities.dockError()}
                forceValidation={forceValidation}
                data-testid="shipping-dock-question"
              />
              {loadingCapabilities.locationHasDock ===
                LoadingCapabilitiesAvailability.No && (
                <LoadingCapabilitiesDropdown
                  question="Does this location have a forklift?"
                  value={
                    loadingCapabilities.locationHasForklift ??
                    LoadingCapabilitiesAvailability.Unknown
                  }
                  onChange={loadingCapabilities.onForkliftChange}
                  errorMessage={loadingCapabilities.forkliftError()}
                  forceValidation={forceValidation}
                  data-testid="forklift-question"
                />
              )}

              <BookShipmentRequoteWithLiftGateSection
                shipment={shipment}
                context={context}
                locationHasDock={loadingCapabilities.locationHasDock}
                locationHasForklift={loadingCapabilities.locationHasForklift}
                contact={props.contact}
                referenceNumber={props.referenceNumber}
                amazonReferenceId={props.amazonReferenceId}
                amazonShipmentId={props.amazonShipmentId}
                boothNumber={props.boothNumber}
                deadlineDate={props.deadlineDate}
                markLocationAsFavourite={props.markLocationAsFavourite}
                isContactNotRequired={props.isContactNotRequired}
                savedLocations={props.savedLocations}
                onModify={props.onModify}
              />
            </div>
          )}
        </div>
      </Panel>
      <div className="h-4" />
      <div className="flex flex-row items-center gap-2">
        <Switch
          checked={props.favouriteSwitchChecked}
          onChange={props.onMarkLocationAsFavourite}
        />
        <div className="flex flex-col items-start">
          <T size="base" weight="semibold">
            Make this a favourite in your address book?{" "}
            <LocationsFavouriteMarker />
          </T>
          <T size="sm" color="muted">
            Make it a favourite if you plan to ship to/from this location
            frequently
          </T>
        </div>
      </div>
    </BookShipmentSmallSection>
  );
}
